// src/components/StringMethodsReference.js
import React from 'react';
import { X, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const StringMethodsReference = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const stringMethods = [
    {
      category: "Basic Information",
      methods: [
        { signature: "int length()", description: "Returns the length of the string" },
        { signature: "boolean isEmpty()", description: "Returns true if length is 0" },
        { signature: "char charAt(int index)", description: "Returns the character at the specified index" },
      ]
    },
    {
      category: "Substring & Extraction",
      methods: [
        { signature: "String substring(int beginIndex)", description: "Returns substring from beginIndex to end" },
        { signature: "String substring(int beginIndex, int endIndex)", description: "Returns substring from beginIndex to endIndex (exclusive)" },
        { signature: "char[] toCharArray()", description: "Converts string to a character array" },
      ]
    },
    {
      category: "Searching",
      methods: [
        { signature: "int indexOf(String str)", description: "Returns index of first occurrence of str, or -1" },
        { signature: "int indexOf(String str, int fromIndex)", description: "Returns index of first occurrence of str starting from fromIndex" },
        { signature: "int lastIndexOf(String str)", description: "Returns index of last occurrence of str, or -1" },
        { signature: "boolean contains(CharSequence s)", description: "Returns true if string contains the sequence" },
        { signature: "boolean startsWith(String prefix)", description: "Returns true if string starts with prefix" },
        { signature: "boolean endsWith(String suffix)", description: "Returns true if string ends with suffix" },
      ]
    },
    {
      category: "Comparison",
      methods: [
        { signature: "boolean equals(Object obj)", description: "Compares this string to another object" },
        { signature: "boolean equalsIgnoreCase(String str)", description: "Compares strings ignoring case" },
        { signature: "int compareTo(String str)", description: "Compares two strings lexicographically" },
      ]
    },
    {
      category: "Case Conversion",
      methods: [
        { signature: "String toLowerCase()", description: "Converts all characters to lowercase" },
        { signature: "String toUpperCase()", description: "Converts all characters to uppercase" },
      ]
    },
    {
      category: "Trimming & Whitespace",
      methods: [
        { signature: "String trim()", description: "Removes leading and trailing whitespace" },
        { signature: "String strip()", description: "Removes leading and trailing whitespace (Unicode-aware)" },
      ]
    },
    {
      category: "Replacement",
      methods: [
        { signature: "String replace(char oldChar, char newChar)", description: "Replaces all occurrences of oldChar with newChar" },
        { signature: "String replace(CharSequence target, CharSequence replacement)", description: "Replaces all occurrences of target with replacement" },
        { signature: "String replaceAll(String regex, String replacement)", description: "Replaces all matches of regex with replacement" },
        { signature: "String replaceFirst(String regex, String replacement)", description: "Replaces first match of regex with replacement" },
      ]
    },
    {
      category: "Splitting & Joining",
      methods: [
        { signature: "String[] split(String regex)", description: "Splits string around matches of regex" },
        { signature: "String[] split(String regex, int limit)", description: "Splits string with limit on number of splits" },
        { signature: "static String join(CharSequence delimiter, CharSequence... elements)", description: "Joins elements with delimiter" },
      ]
    },
    {
      category: "Concatenation",
      methods: [
        { signature: "String concat(String str)", description: "Concatenates specified string to end of this string" },
        { signature: "String + operator", description: "Concatenates strings using + operator" },
      ]
    },
    {
      category: "Formatting & Conversion",
      methods: [
        { signature: "static String valueOf(int i)", description: "Returns string representation of int" },
        { signature: "static String valueOf(double d)", description: "Returns string representation of double" },
        { signature: "static String valueOf(boolean b)", description: "Returns string representation of boolean" },
        { signature: "static String format(String format, Object... args)", description: "Returns formatted string using format specifiers" },
      ]
    },
    {
      category: "Other Useful Methods",
      methods: [
        { signature: "String repeat(int count)", description: "Returns string repeated count times" },
        { signature: "boolean matches(String regex)", description: "Returns true if string matches regex" },
        { signature: "boolean isBlank()", description: "Returns true if string is empty or contains only whitespace" },
      ]
    }
  ];

  return (
    <>
      {/* Dark overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="bg-mad-timberwolf border-mad-gunmetal/50 shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-mad-fulvous text-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Java String Methods Reference</h2>
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto p-6 space-y-6">
            <p className="text-gray-700 text-sm mb-4">
              Quick reference for Java String methods. Use these to solve your string problems!
            </p>

            {stringMethods.map((category, idx) => (
              <div key={idx} className="space-y-3">
                <h3 className="text-lg font-bold text-mad-fulvous border-b-2 border-mad-fulvous/30 pb-1">
                  {category.category}
                </h3>
                <div className="space-y-2">
                  {category.methods.map((method, methodIdx) => (
                    <div 
                      key={methodIdx}
                      className="bg-white/50 rounded-lg p-3 border border-mad-gunmetal/20 hover:border-mad-fulvous/50 transition-colors"
                    >
                      <code className="text-sm font-mono text-mad-dart-green font-semibold">
                        {method.signature}
                      </code>
                      <p className="text-gray-700 text-sm mt-1 ml-4">
                        {method.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Footer note */}
            <div className="bg-mad-fulvous/10 border border-mad-fulvous/30 rounded-lg p-4 mt-6">
              <p className="text-sm text-gray-800">
                <strong>💡 Tip:</strong> Remember that strings in Java are immutable. Methods like <code className="bg-white/50 px-1 rounded">toLowerCase()</code> return a new string rather than modifying the original.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
};

export default StringMethodsReference;
